import {

    Map,
} from 'leaflet';
import axios from 'axios';
import * as Constants from "../../constants"
import { LeafMap } from '../../assets/js/abstract_leaflet_map';
import rainJSON from './rain.json';
import frainJSON from './frain.json';
import mixJSON from './mix.json';
import snowJSON from './snow.json';
import emptyJSON from './empty.json';
import { Mat } from './Mat';
import { createDbzLoadHandler, initColortables } from './module';

export class NationalRadarService {
    map: Map;
    zr_map: LeafMap ;
    tileCoordinates: any[] = [];
    imageNames: any[] = [];
    isReady: boolean = false;
    colorTables: any= { rain: null, frain: null, mix: null, snow: null, empty: null }
    ignoreRadar: boolean =  false;
    isLoadingXML: boolean =  false;
    syncTimeout: any =  0;
    maxFrames: number = 9; //30 min loop
    allFrames: boolean = false; // include all frames
    skipFrames: boolean = true; // skip some frames
    loadLock: boolean = false;
    frameIndex: number = 0;
    loadLockTimeout: any = null;
    allTiles: any[] = [];
    isAnimating: boolean = false;
    autoLoop: boolean = false;
    createAnimationOverlays: boolean = false;
    tileStatuses: any [] = [];
    animatingTimeout: any = null;
    inputTimeout: any = 0;
    bounds = {
        feedMaxLat: 50.303131,
        feedMinLat: 22.063271,
        feedMaxLon: -64.575134,
        feedMinLon: -128.424866
    }
    radarSize = {
        width: 14200,
        height: 7956
    }
    maxZoomLevel: number = 16;
    tileSizes = {
        width: 350,
        height: 350
    }
    dragLock: boolean = false;
    tileOverlays: any[] = [];
    tileOpacity = 0.75;
    tileUrl:string = Constants.NATIONAL_TILE_URL ?? '';
    feedUrl:string = Constants.NATIONAL_API_URL ?? '';
    tileCoordinatesUrl:string = '';
    autoRefresh: boolean = false;
    loadFunction: any;
    errorFunction: any;
    updateFromUrl: boolean = false;
    frameLoadQueue: number[] = [];
    activeRequests: AbortController[] = []
    private updateTimestamp: (timestamp: string) => void = () => {};

    constructor(map: Map, useLargeFeed: boolean, autoRefresh: boolean, allFrames: boolean, skipFrames: boolean, animate: boolean, autoLoop: boolean, updateTimestamp: (timestamp: string) => void) {
        this.map = map;
        this.autoRefresh = autoRefresh;
        this.allFrames = allFrames;
        this.skipFrames = skipFrames;
        if (useLargeFeed) {
            this.radarSize.width = 28431;
            this.radarSize.height = 15919;
            this.maxZoomLevel = 64;
            this.feedUrl = Constants.NATIONAL_API_URL + '?s3=1' ?? '';
            this.tileCoordinatesUrl = Constants.NATIONAL_TILE_COORDINATES_JSON ?? '';
        }
        
        this.zr_map = new LeafMap(map,{});
        this.tileOpacity = 1;
        this.imageNames = [];
        this.autoLoop= autoLoop;
        this.isAnimating= animate;
        this.updateTimestamp = updateTimestamp;
        
    }
    
    abortPendingRequests() {
        this.activeRequests.forEach(controller => {
            controller.abort();
        });
        this.activeRequests = [];
    }
    
    async init(minDbz: number = 0, opacity: number) {
        try{
            this.tileOpacity = (opacity/100);
            
            const response = await axios.get(this.tileCoordinatesUrl ?? '');
            this.tileCoordinates = response.data;
            this.isReady = true;

            await this.finalInit();


            if (Mat.ready()) {
                console.log("Mat is ready!");
                await initColortables(this, rainJSON, frainJSON, mixJSON, snowJSON, emptyJSON, minDbz);

            }
        }catch (e){
            console.error(e);
        }
        
        
    }

    async finalInit() {
        if (!this.isReady) return;

        // Check that each colortable is loaded
        for (const prop of Object.getOwnPropertyNames(this.colorTables)) {
            if (!this.colorTables[prop])
                return;
        }

        await this.syncJSON();
		
        if (this.isAnimating){
            this.startAnimation();
        }else{
			this.showFrame();
		}
    }

    async syncJSON() {
        if (this.ignoreRadar) return;
        await this.syncJSONForced(false);
    }

    async syncJSONForced(forcedRefresh: boolean) {
        if (this.ignoreRadar) return;

        if (this.isLoadingXML && !forcedRefresh) return;

        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
            this.syncTimeout = null;
        }

        this.isLoadingXML = true;
        try {
            const response = await axios.get(this.feedUrl  ?? '');
			const lastImageName = this.imageNames.length > 0 ? this.imageNames[0] : "";
			this.imageNames = [];
			if(response.data && response.data.length > 0){
				this.imageNames = response.data;
			}
            
			//No longer need to remove frames since switching to S3
            //Remove last frame to give it time to sync images
            //this.imageNames.shift();
            //Temporary remove another frame due to server overload
            //this.imageNames.pop();

            //Default to 30 min loop, if available
            if (!this.allFrames && this.maxFrames > this.imageNames.length) {
                this.imageNames = this.imageNames.slice(0, this.maxFrames);
            }
            if (this.allFrames && this.skipFrames) {
                const newFrames: any[] = [];
                this.imageNames.forEach((image, index) => {
                    if (index % 2 == 0) newFrames.push(image);
                });

                this.imageNames = newFrames;
            }
            
            this.isLoadingXML = false;
            
            
            if (forcedRefresh) { //If forced then show frame
                this.showFrame();
            } else if(!lastImageName || lastImageName != this.imageNames[0]){ //If the last image doesn't exist or changed then refresh
                this.updateTimestamp(this.imageNames[0]);
                this.refresh(false);
            }

            //auto-refresh on new frame images
            if (this.autoRefresh && this.isAnimating && lastImageName && lastImageName != this.imageNames[0]) {
                this.stopAnimation();
                setTimeout( () => {
                    this.startAnimation();
                }, 300);
            }

        } catch (e) {
            this.isLoadingXML = false;
        }
        this.syncTimeout = setTimeout(()=>{
            this.syncJSON();
        }, 20000);
    }
	
    startAnimation() {
        const event = new CustomEvent('loadingStart');
        window.dispatchEvent(event);
        if (this.ignoreRadar) return;

        // Resetting animation state to prevent mutiple radar layer
        this.frameIndex = 0;
        this.frameLoadQueue = [];
        this.tileStatuses = [];

        this.tileOverlays = [];
        this.loadLock = false;
    
        if (this.loadLockTimeout) {
            clearTimeout(this.loadLockTimeout);
            this.loadLockTimeout = null;
        }
    
        this.createAnimationOverlays = true;
        this.isAnimating = true;
        this.showFrame();
    }

    stopAnimation () {
        this.abortPendingRequests();
        if (this.ignoreRadar) return;
        const event = new CustomEvent('loadingFinished');
        window.dispatchEvent(event);
        this.createAnimationOverlays = false;
        this.frameIndex = 0;
        this.isAnimating = false;
        this.loadLock = false;

        // Clearing all tile overlays - Prevent animation layers loading after the animation is stopped
        if (this.tileOverlays.length > 0) {
            for (let mI = 0; mI < this.tileOverlays.length; mI++) {
                if (this.tileOverlays[mI]) {
                    for (let mX = 0; mX < this.tileOverlays[mI].length; mX++) {
                        if (this.tileOverlays[mI][mX]) {
                            for (let mY = 0; mY < this.tileOverlays[mI][mX].length; mY++) {
                                if (this.tileOverlays[mI][mX][mY]) {
                                    this.map.removeLayer(this.tileOverlays[mI][mX][mY]);
                                }
                            }
                        }
                    }
                }
            }
        }

        this.tileOverlays = [];
        this.tileStatuses = [];
        this.allTiles = [];
        
        // Clear any pending timeouts
        if (this.loadLockTimeout) {
            clearTimeout(this.loadLockTimeout);
            this.loadLockTimeout = null;
        }
        
        if (this.syncTimeout) {
            clearTimeout(this.syncTimeout);
            this.syncTimeout = null;
        }

        this.showFrame();
    }
	
    /** Refreshes. **/
    refresh = (forced: any) => {
        
        if (this.ignoreRadar) return;
        if (forced) {
            this.loadLock = false;
            this.showFrame();
        } else {
            if (this.loadLock || this.dragLock || this.isAnimating) {
                return;
            }
            this.showFrame();
        }
    }
    
    changeDrag(dragValue: boolean) {
        this.dragLock = dragValue;
    }

    pauseAnimationOnTransition ()  {
        if (this.isAnimating && this.autoLoop) {
            this.stopAnimation();
        }
        if (this.inputTimeout){
            clearTimeout(this.inputTimeout);
        }
        this.inputTimeout = setTimeout( () => {
            if (this.autoLoop) {
                this.startAnimation();
            }
        }, 2000);
        
    }

    getFrameLoadOrder(totalFrames: number): number[] {
        const order: number[] = [0];
        if (totalFrames <= 1) return order;

        // a stupid way to get load order 
        let step: number = (totalFrames / 2) | 0;
        while (step > 0) {
            for (var i = 0; i < totalFrames; i += step) {
                if(!order.includes(i))
                     order.push(i); 
            }

            step = (step / 2) | 0;
        }

        return order;
    }

    showFrame() {
        if (this.ignoreRadar) return;
        if (this.loadLock) return;

        if (!this.imageNames || this.imageNames.length <= this.frameIndex || !this.imageNames[this.frameIndex]) {
            const event = new CustomEvent('loadingFinished');
            window.dispatchEvent(event);
            return;
        };

        this.loadLock = true;
        if (this.loadLockTimeout)
            clearTimeout(this.loadLockTimeout);
        this.loadLockTimeout = setTimeout(()=>{
            this.releaseLoadLock();
            const event = new CustomEvent('loadingFinished');
            window.dispatchEvent(event);
        }, 10000);

        if (this.animatingTimeout) {
            clearTimeout(this.animatingTimeout);
            this.animatingTimeout = null;
        }
        
        let mI, mX, mY;
        let createOverlays = false;
        let allImagesLoaded = false;
        let removeOverlays = false;
        if (this.isAnimating && this.createAnimationOverlays) {
            createOverlays = true;
            this.createAnimationOverlays = false;

            if (this.tileStatuses.length > 0)
                removeOverlays = true;
        } else if (this.isAnimating) {
            allImagesLoaded = true;
            for (mI = 0; mI < this.imageNames.length - 1; mI++) {
                if (!this.tileStatuses[mI]) {
                    allImagesLoaded = false;
                    break;
                }
            }
        } else {
            if (this.tileStatuses.length > 0)
                removeOverlays = true;
        }

        const boundsMap = this.map.getBounds();

        const mapURLat =  boundsMap.getNorth();
        const mapURLon = boundsMap.getEast();
        const mapLLLat = boundsMap.getSouth();
        const mapLLLon = boundsMap.getWest();

        let mapUL = this.zr_map.llToPix(
            
            this.zr_map.newLLPoint(mapURLat, mapLLLon)
        );
        let mapLR = this.zr_map.llToPix(
            this.zr_map.newLLPoint(mapLLLat, mapURLon)
        );
        let topLeftCorner: any = this.zr_map.llToPix(
            this.zr_map.newLLPoint(this.bounds.feedMaxLat, this.bounds.feedMinLon)
        );
        // fix for line between tiles
        topLeftCorner = { x: Math.ceil(topLeftCorner.x), y: Math.ceil(topLeftCorner.y) };

        let bottomRightCorner: any = this.zr_map.llToPix(
            this.zr_map.newLLPoint(this.bounds.feedMinLat, this.bounds.feedMaxLon)
        );
        // fix for line between tiles
        bottomRightCorner = { x: Math.floor(bottomRightCorner.x), y: Math.floor(bottomRightCorner.y) };

        var targetSize = Math.abs(bottomRightCorner.x - topLeftCorner.x);
        var zoomLevel = 1;
        while ((targetSize / 3.5) < this.radarSize.width / zoomLevel)
            zoomLevel *= 2;

        if (zoomLevel > this.maxZoomLevel) zoomLevel = this.maxZoomLevel;

        var currentRadarWidth = this.radarSize.width / zoomLevel;
        var currentRadarHeight = this.radarSize.height / zoomLevel;

        var correctionRatio = currentRadarWidth / targetSize;
        var minX = (mapUL.x - topLeftCorner.x) * correctionRatio;
        var minY = (mapUL.y - topLeftCorner.y) * correctionRatio;
        var maxX = (mapLR.x - topLeftCorner.x) * correctionRatio;
        var maxY = (mapLR.y - topLeftCorner.y) * correctionRatio;

        var tileMinX = Math.floor(Math.max(minX - this.tileSizes.width, 0) / this.tileSizes.width);
        var tileMinY = Math.floor(Math.max(minY - this.tileSizes.height, 0) / this.tileSizes.height);
        var tileMaxX = Math.floor(Math.min(maxX + this.tileSizes.width, currentRadarWidth) / this.tileSizes.width);
        var tileMaxY = Math.floor(Math.min(maxY + this.tileSizes.height, currentRadarHeight) / this.tileSizes.height);

        var xCnt = tileMaxX - tileMinX + 1;
        var yCnt = tileMaxY - tileMinY + 1;
        var tileCnt = xCnt * yCnt;
        var tilesLoaded = 0;

        // fix for line between tiles
        var correctionRatioX = correctionRatio * (1.0007);
        var correctionRatioY = correctionRatio * (1.0018);
        if (!this.tileCoordinates) {
            correctionRatioX = correctionRatio * (1.002);
            correctionRatioY = correctionRatio * (1.002);
        }
        correctionRatio = correctionRatio * 1.002;

        if (allImagesLoaded) {
            //console.log('allImagesLoaded');
            this.allTilesLoaded(xCnt, yCnt);
            const event = new CustomEvent('loadingFinished');
            window.dispatchEvent(event);
            return;
        }
        
        if (removeOverlays) {
            
            this.tileStatuses = [];
            for (mI = 0; mI < this.tileOverlays.length; mI++) {
                for (mX = 0; mX < this.tileOverlays[mI].length; mX++) {
                    for (mY = 0; mY < this.tileOverlays[mI][mX].length; mY++) {
                        this.map.removeLayer(this.tileOverlays[mI][mX][mY]);
                    }
                }
            }
            this.tileOverlays = [];
        }
        
        if (createOverlays) {
   
            for (var tI = 0; tI < this.allTiles.length; tI++) {
                this.map.removeLayer(this.allTiles[tI]);
            }
            //this.allTiles = []; // todo: ??

            this.tileOverlays = [];
            this.tileStatuses = [];
            this.frameLoadQueue = this.getFrameLoadOrder(this.imageNames.length - 1);
            for (mI = 0; mI < this.imageNames.length - 1; mI++) {
                this.tileOverlays[mI] = [];
                this.tileStatuses[mI] = false;
            }
        } else if(!this.isAnimating) {

            const tempAllTiles: any [] = [];
            for (let tI = 0; tI < this.allTiles.length; tI++) {
                tempAllTiles.push(this.allTiles[tI]);
            }

            this.allTiles = [];

            this.updateFromUrl = true;
            var updateOnLoadAll = true;
            var tilesOverlayLoaded = 0;
            
            const loadedFunction =  () => {
                tilesOverlayLoaded++;
                if (tilesOverlayLoaded == tileCnt) {
                    for (var i = 0; i < this.allTiles.length; i++) {
                        if (this.allTiles[i]) {
                            this.allTiles[i].setOpacity(this.tileOpacity);
                            this.allTiles[i].getElement().hidden = false;
                            this.allTiles[i].getElement().classList.add('loaded-layer');
                        }
                    }

                    for (var i = 0; i < tempAllTiles.length; i++) {
                        if (tempAllTiles[i]) {
                            URL.revokeObjectURL(tempAllTiles[i].getElement().src);
                            this.map.removeLayer(tempAllTiles[i]);
                            delete tempAllTiles[i];
                        }
                    }

                    this.loadLock = false;
                }
            };
            

             this.loadFunction = createDbzLoadHandler(this, updateOnLoadAll ? loadedFunction : null,  this.updateFromUrl);
             this.errorFunction = updateOnLoadAll ? loadedFunction : null;
            
        }
        
        var tileCoords_sw_ne: any[][] = []; // for animation
        for (var x = 0; x < xCnt; x++) {
            tileCoords_sw_ne[x] = [];
            
            for (var y = 0; y < yCnt; y++) {
                let shapePoints;
                
                if (this.tileCoordinates) {
                    
                    var tileBounds = this.tileCoordinates[zoomLevel][tileMinX + x][tileMinY + y];

                    shapePoints = [tileBounds[1], tileBounds[2], tileBounds[0], tileBounds[3]];
                    
                    //Defines deltas per zoom level
                    var deltaX = 0;
                    var deltaY = 0;
                    if (this.map.getZoom() <= 3) {
                        deltaX = 0;
                        deltaY = 0;
                    } else if (this.map.getZoom() <= 4) {
                        deltaX = 1;
                        deltaY = 0;
                    } else if (this.map.getZoom() <= 5) {
                        deltaX = 1;
                        deltaY = 1;
                    } else if (this.map.getZoom() <= 7) {
                        deltaX = 1;
                        deltaY = 2;
                    } else if (this.map.getZoom() <= 8) {
                        deltaX = 1;
                        deltaY = 1;
                    } else if (this.map.getZoom() <= 9) {
                        deltaX = 1;
                        deltaY = 1;
                    }
                    
                    //If next horizontal tile exists
                    if (this.tileCoordinates[zoomLevel][tileMinX + x][tileMinY + y + 1]) {
                        
                        var nextHorizontalTile = this.tileCoordinates[zoomLevel][tileMinX + x][tileMinY + y + 1];
                        var nextHorizontalTileNE = this.zr_map.newLLPoint(nextHorizontalTile[1], nextHorizontalTile[3]);
                        var nextHorizontalTileXY = this.zr_map.llToPix(nextHorizontalTileNE);
                        var currentTileHorizontalSE = this.zr_map.pixToLL(this.zr_map.newXYPoint(nextHorizontalTileXY.x, nextHorizontalTileXY.y + deltaY));
                        shapePoints[2] = currentTileHorizontalSE.lat();
                    }
                    //If next vertical tile exists
                    if (this.tileCoordinates[zoomLevel][tileMinX + x + 1] && this.tileCoordinates[zoomLevel][tileMinX + x + 1][tileMinY + y]) {
                       
                        var nextVerticalTile = this.tileCoordinates[zoomLevel][tileMinX + x + 1][tileMinY + y];
                        var nextVerticalTileNE = this.zr_map.newLLPoint(nextVerticalTile[1], nextVerticalTile[2]);
                        var nextVerticalTileXY = this.zr_map.llToPix(nextVerticalTileNE);
                        
                        var currentTileVerticalSE = this.zr_map.pixToLL(this.zr_map.newXYPoint(nextVerticalTileXY.x + deltaX, nextVerticalTileXY.y));
                        shapePoints[3] = currentTileVerticalSE.lng();
                        
                    }
                    
                } else {
                    var minPixX = (tileMinX + x) * 350 / correctionRatioX + topLeftCorner.x;
                    var minPixY = (tileMinY + y) * 350 / correctionRatioY + topLeftCorner.y;
                    var maxPixX = minPixX + 350 / correctionRatioX;
                    var maxPixY = minPixY + 350 / correctionRatioY;

                    var minLL = this.zr_map.pixToLL(
                        this.zr_map.newXYPoint(minPixX, minPixY)
                    );
                    var maxLL = this.zr_map.pixToLL(
                        this.zr_map.newXYPoint(maxPixX, maxPixY)
                    );

                    var tileMinLat = minLL.lat();
                    var tileMaxLat = maxLL.lat();
                    var tileMinLon = minLL.lng();
                    var tileMaxLon = maxLL.lng();

                    shapePoints = [tileMinLat, tileMinLon, tileMaxLat, tileMaxLon];
                }
                
                var sw_latlon = this.zr_map.newLLPoint(shapePoints[2], shapePoints[1]);//new google.maps.LatLng(module.tileset[x][y].shapePoints[2], module.tileset[x][y].shapePoints[1]);
                var ne_latlon = this.zr_map.newLLPoint(shapePoints[0], shapePoints[3]);//new google.maps.LatLng(module.tileset[x][y].shapePoints[0], module.tileset[x][y].shapePoints[3]);

                if (!this.isAnimating)
                {
                    let tileUrl = this.tileUrl + this.imageNames[this.frameIndex] + "/" + zoomLevel + "/" + (tileMinY + y) + "/" + (tileMinX + x) + ".webp";
                    const overlay: any = this.zr_map.newGroundOverlay(tileUrl, sw_latlon, ne_latlon, 0, false, this.loadFunction, true, this.errorFunction,  this.updateFromUrl);//new google.maps.GroundOverlay(imageURL, new google.maps.LatLngBounds(sw_latlon,ne_latlon), {clickable: false});
                    overlay.dbzColored = false;
                    this.allTiles.push(overlay);

                } else if(createOverlays) {
                    // just save coords for later use
                    tileCoords_sw_ne[x][y] = [sw_latlon, ne_latlon];
                }
            }
        }

        if (createOverlays) {
            // Moved from html img to Image object to handle abort request
            const controller = new AbortController();
            this.activeRequests.push(controller);

            for (mI = 0; mI < this.imageNames.length - 1; mI++) {
                var i = this.frameLoadQueue[mI];

                const createLoadedFunctionAnimated = (i: number) => {
                    var animatedTilesOverlayLoaded = 0;
                    return () => {
                        animatedTilesOverlayLoaded++;
                        if (animatedTilesOverlayLoaded == tileCnt) {
                            //console.log('animation slice loaded %d', i);
                            this.tileStatuses[i] = true;

                            for (mX = 0; mX < xCnt; mX++) {
                                for (mY = 0; mY < yCnt; mY++) {
                                    if (this.tileOverlays[i] && this.tileOverlays[i][mX] && this.tileOverlays[i][mX][mY]) {
                                        this.tileOverlays[i][mX][mY].getElement().hidden = false;
                                        this.tileOverlays[i][mX][mY].getElement().classList.add('loaded-layer');
                                    }
                                }
                            }

                            this.allTilesLoaded(xCnt, yCnt);
                        }
                    };
                };

                var updateOnLoadAll = true;
                const loadedFunctionAnimated = createLoadedFunctionAnimated(i);
                const loadFunctionAnimated = createDbzLoadHandler(this, updateOnLoadAll ? loadedFunctionAnimated : null,  this.updateFromUrl);
                const errorFunctionAnimated = updateOnLoadAll ? loadFunctionAnimated : null;

                //console.log('index/order %d/%d', mI, i);
                for (x = 0; x < xCnt; x++) {
                    this.tileOverlays[i][x] = [];
                    for (y = 0; y < yCnt; y++) {
                        if (controller.signal.aborted) break;
                        var frameUrl = this.tileUrl + this.imageNames[i] + "/" + zoomLevel + "/" + (tileMinY + y) + "/" + (tileMinX + x) + ".webp";
                        const overlay = this.zr_map.newGroundOverlay(frameUrl, tileCoords_sw_ne[x][y][0], tileCoords_sw_ne[x][y][1], 0, false, loadFunctionAnimated, true, errorFunctionAnimated,  this.updateFromUrl);
                        this.tileOverlays[i][x][y] = overlay;
                    }
                }
            }
        }
    }

    allTilesLoaded (xCnt: number, yCnt: number) {
        if (this.ignoreRadar) return;   
        if (!this.isAnimating) return;
        if (this.animatingTimeout) return;

        if (this.dragLock) {
            this.loadLock = false;
            return;
        }

        if (this.tileStatuses[this.frameIndex]) { // to prevent blinking
            var mI, mX, mY;
            for (mI = 0; mI < this.imageNames.length - 1; mI++) {
                for (mX = 0; mX < xCnt; mX++) {
                    for (mY = 0; mY < yCnt; mY++) {
                        if (this.tileOverlays[mI] && this.tileOverlays[mI][mX] && this.tileOverlays[mI][mX][mY])
                            this.tileOverlays[mI][mX][mY].setOpacity(mI == this.frameIndex ? this.tileOpacity : 0);
                    }
                }
            }
        }

        this.loadLock = false;
        this.frameIndex--;
        if (this.frameIndex < 0) {
            this.frameIndex = this.imageNames.length - 2; // need to skip last frame as it can be cleared
            this.animatingTimeout = setTimeout(()=> {
                this.showFrame();
            }, 250); // old value was 1000
        }
        else {
            this.animatingTimeout = setTimeout(()=>{
                this.showFrame();
            }, 250);
        }
    }
    
    setOpacity (value: number) {
        if (this.ignoreRadar) return;
        this.tileOpacity = value/100;
        if (this.isAnimating) return;
        for (const tile of this.allTiles) {
            if (tile.dbzColored) {
                tile.setOpacity(value/100);
            }
        }
    }
    
    releaseLoadLock () {
        if (this.ignoreRadar) return;
        if (this.loadLock) {
            this.loadLock = false;
            this.allTiles.forEach((tile) => {
                if (tile) {
                    if (tile.getElement()) {
                        URL.revokeObjectURL(tile.getElement().src);
                    }
                    this.map.removeLayer(tile);
                }
            })

            this.allTiles = [];
        
            const event = new CustomEvent('loadingFinished');
            window.dispatchEvent(event);

        }
    }
    
    async changeDbz(value: number) {
        await initColortables(this, rainJSON, frainJSON, mixJSON, snowJSON, emptyJSON, value);
    }
}
